import { useMemo, useState } from 'react';
import { Box, Button, HStack, Input, Text, Textarea } from '@chakra-ui/react';
import { RiForbid2Line } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { DataTable } from '../_ui/DataTable';
import { FilterBar } from '../_ui/FilterBar';
import { ConfirmDialog, SectionCard } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import {
	useListSuppressionsQuery,
	useAddSuppressionMutation,
	useImportSuppressionsMutation,
	useRemoveSuppressionMutation,
} from 'api/metaWhatsappApi';
import campaignError from '../campaignErrors';

/**
 * The do-not-message list.
 *
 * ★ REMOVING SOMEBODY IS THE DANGEROUS ACTION HERE, NOT ADDING THEM. Adding
 * only stops messages; removing can restart them to a person who asked them to
 * stop. So a removal requires a written reason — the server refuses without
 * one — and the dialog says plainly what it is about to allow.
 */

const PAGE_SIZE = 50;

/** Why somebody is on the list. */
const REASON = {
	opted_out: 'They asked to stop',
	blocked: 'They blocked us',
	hard_bounce: 'WhatsApp said the number cannot receive',
	manual: 'Added by hand',
};

export default function Suppressions() {
	const ui = useCampaignUI();
	const notify = useAppToast();

	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [single, setSingle] = useState('');
	const [bulk, setBulk] = useState('');
	const [removing, setRemoving] = useState(null);

	const { data, isLoading, isFetching, isError, refetch } = useListSuppressionsQuery({
		...(search ? { search } : {}),
		page,
		pageSize: PAGE_SIZE,
	});

	const [add, addState] = useAddSuppressionMutation();
	const [importList, importState] = useImportSuppressionsMutation();
	const [remove, removeState] = useRemoveSuppressionMutation();

	const onAdd = async () => {
		const number = single.trim();
		if (!number) return;
		try {
			await add({ number, reason: 'manual' }).unwrap();
			setSingle('');
			notify.success('Added to the list');
		} catch (err) {
			notify.error('Could not add that number', campaignError(err));
		}
	};

	const onImport = async () => {
		const text = bulk.trim();
		if (!text) return;
		try {
			const report = await importList({ text, reason: 'manual' }).unwrap();
			setBulk('');
			// ★ The row report is the point of a bulk import. An operator handed a
			// legal opt-out list must know which lines did not take.
			notify.success(
				`${report.added} added`,
				report.invalid ? `${report.invalid} line(s) could not be read as a phone number.` : undefined,
			);
		} catch (err) {
			notify.error('Import failed', campaignError(err));
		}
	};

	const columns = useMemo(
		() => [
			{
				key: 'number',
				header: 'Number',
				cell: (row) => (
					<Text fontSize='sm' color={ui.textPrimary}>
						{row.e164 || `+${row.waId}`}
					</Text>
				),
			},
			{
				key: 'reason',
				header: 'Why',
				cell: (row) => (
					<Box>
						<Text fontSize='sm' color={ui.textSecondary}>
							{REASON[row.reason] ?? row.reason}
						</Text>
						{row.source?.keyword && (
							<Text fontSize='xs' color={ui.textMuted}>
								they wrote “{row.source.keyword}”
							</Text>
						)}
					</Box>
				),
			},
			{
				key: 'at',
				header: 'Since',
				cell: (row) => (
					<Text fontSize='sm' color={ui.textMuted}>
						{row.at ? new Date(row.at).toLocaleDateString() : '—'}
						{row.expiresAt ? (
							<Text as='span' display='block' fontSize='xs'>
								until {new Date(row.expiresAt).toLocaleDateString()}
							</Text>
						) : null}
					</Text>
				),
			},
			{
				key: 'actions',
				header: '',
				cell: (row) => (
					<Button size='xs' variant='ghost' color={ui.danger} onClick={() => setRemoving(row)}>
						Remove
					</Button>
				),
			},
		],
		[ui],
	);

	return (
		<Box>
			<SectionCard
				title='Add to the list'
				subtitle='Anyone here is skipped by every campaign, for good, unless somebody deliberately removes them.'
				mb={5}
			>
				<HStack spacing={3} mb={4} align='flex-start' wrap='wrap'>
					<Input
						size='sm'
						maxW='260px'
						placeholder='+971 50 123 4567'
						value={single}
						onChange={(e) => setSingle(e.target.value)}
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
					/>
					<Button size='sm'   isLoading={addState.isLoading} onClick={onAdd} variant='brand'>
						Add
					</Button>
				</HStack>

				<Text fontSize='sm' color={ui.textSecondary} mb={2}>
					Or paste a list — one number per line.
				</Text>
				<Textarea
					size='sm'
					rows={4}
					value={bulk}
					onChange={(e) => setBulk(e.target.value)}
					placeholder={'+971501234567\n+923001234567'}
					bg={ui.inputBg}
					borderColor={ui.border}
					color={ui.textPrimary}
					mb={3}
				/>
				<Button
					size='sm'
					variant='neutralOutline'
					borderColor={ui.border}
					color={ui.textPrimary}
					isLoading={importState.isLoading}
					onClick={onImport}
				>
					Import list
				</Button>
			</SectionCard>

			<SectionCard title='On the list' p={0}>
				<FilterBar>
					<Input
						size='sm'
						maxW='260px'
						placeholder='Search by number'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
					/>
					<Text fontSize='sm' color={ui.textMuted} ml='auto'>
						{Number(data?.total ?? 0).toLocaleString()} number(s)
					</Text>
				</FilterBar>

				<DataTable
					columns={columns}
					rows={data?.rows ?? []}
					rowKey={(row) => row.waId}
					isLoading={isLoading}
					isFetching={isFetching}
					isError={isError}
					onRetry={refetch}
					emptyState={{
						icon: RiForbid2Line,
						title: 'Nobody is suppressed',
						description: 'People who reply STOP are added here automatically.',
					}}
					pagination={{
						page,
						total: data?.total ?? 0,
						limit: PAGE_SIZE,
						totalPages: Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE)),
						onPageChange: setPage,
					}}
				/>
			</SectionCard>

			<ConfirmDialog
				isOpen={Boolean(removing)}
				onClose={() => setRemoving(null)}
				title='Take this number off the list?'
				description={`${removing?.e164 || ''} will be able to receive campaign messages again. If they asked to stop, this undoes that.`}
				confirmLabel='Remove'
				isLoading={removeState.isLoading}
				requireReason
				reasonLabel='Why is this being removed?'
				reasonPlaceholder='e.g. they asked us to start messaging them again'
				onConfirm={async (reason) => {
					const waId = removing?.waId;
					setRemoving(null);
					try {
						await remove({ waId, reason }).unwrap();
						notify.success('Removed from the list');
					} catch (err) {
						notify.error('Could not remove that number', campaignError(err));
					}
				}}
			/>
		</Box>
	);
}
